<template>
  <div
    class="play-stepper"
    :class="[`play-stepper--${size}`, { 'play-stepper--inline': inline, 'is-disabled': disabled }]"
    role="group"
  >
    <button
      type="button"
      class="play-stepper__btn"
      :disabled="disabled || value <= min"
      :aria-label="decrementLabel"
      @click="onStep(-1)"
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
      ><path d="M5 12h14" /></svg>
    </button>

    <span
      class="play-stepper__value"
      role="spinbutton"
      :aria-valuenow="value"
      :aria-valuemin="min"
      :aria-valuemax="max"
    >{{ value }}</span>

    <button
      type="button"
      class="play-stepper__btn"
      :disabled="disabled || value >= max"
      :aria-label="incrementLabel"
      @click="onStep(1)"
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
      ><path d="M12 5v14M5 12h14" /></svg>
    </button>
  </div>
</template>

<script>
/**
 * PlayStepper — numeric +/- spinner for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-form-spinbutton>. Drive it with v-model; `min`/`max`/`step` bound the
 * range (buttons disable at the edges); `inline` lays it out inline. Emits
 * `change` with the new number on every step (matches b-form-spinbutton).
 *
 * VUE3-MIGRATION: `model: { prop: 'value', event: 'input' }` → Vue 3
 * `modelValue` / `update:modelValue`; change this block + the emit name. Props
 * are otherwise Vue 3-safe.
 */
export default {
  name: 'PlayStepper',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Number,
      default: 0,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    step: {
      type: Number,
      default: 1,
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    inline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    decrementLabel: {
      type: String,
      default: 'Decrement',
    },
    incrementLabel: {
      type: String,
      default: 'Increment',
    },
  },
  methods: {
    onStep(dir) {
      if (this.disabled) return
      const next = Math.min(this.max, Math.max(this.min, this.value + dir * this.step))
      if (next === this.value) return
      this.$emit('input', next)
      this.$emit('change', next)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
  width: max-content;

  &--inline { display: inline-flex; }

  &__btn {
    display: grid;
    place-items: center;
    padding: 0;
    color: var(--text-strong);
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-hard-sm);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition:
      transform var(--dur-fast) var(--ease-bounce),
      box-shadow var(--dur-fast) var(--ease-out);

    svg {
      width: 18px;
      height: 18px;
      fill: none;
      stroke: currentColor;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    &:hover:not(:disabled) { box-shadow: var(--shadow-hard); }
    &:active:not(:disabled) { transform: translateY(2px); box-shadow: var(--shadow-press); }
    &:focus-visible { outline: 2px solid var(--c-secondary); outline-offset: 2px; }

    &:disabled { opacity: .4; cursor: not-allowed; }
  }

  &__value {
    min-width: 2.4em;
    font-family: var(--font-display);
    font-weight: 800;
    text-align: center;
    color: var(--text-strong);
    font-variant-numeric: tabular-nums;
  }

  /* ── Sizes ── */
  &--sm {
    .play-stepper__btn { width: 30px; height: 30px; }
    .play-stepper__value { font-size: 15px; }
  }
  &--md {
    .play-stepper__btn { width: 38px; height: 38px; }
    .play-stepper__value { font-size: 17px; }
  }
  &--lg {
    .play-stepper__btn { width: 46px; height: 46px; }
    .play-stepper__value { font-size: 20px; }
  }

  &.is-disabled { opacity: .6; }
}

@media (prefers-reduced-motion: reduce) {
  .play-stepper__btn { transition: none; }
}
</style>
